import { FormEvent, useEffect, useState } from "react";
import styled from "styled-components";
import ToursService from "../../../services/tours.service";
import AllyService from "../../../services/ally.service";
import { useGlobal } from "../../../context/global";
import { useAlly } from "../../../context/ally";
import { Tour, TourPrice } from "../../../types/common.types";
import m from "../../../styles/molanturTheme";
import { Button, DayStrip, Field, Icon, Label, TourThumb, WizardShell, clp } from "../ui";

// Para buscar sin que importen mayúsculas ni tildes ("geiser" encuentra "Géiser").
const plain = (text: string) => text.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')

// Paso 2: qué tour y qué día. Se llega desde el paso 1 (huéspedes recién creados), desde "Crear
// otra reservación" del voucher, o desde "Agregar tour" en Mis reservas (pasajeros de ese grupo).
export default function AddReservationGroup() {
    const { setMessage, setIsLoading } = useGlobal()
    const { newGroup, setNewGroup, newGroupReservationData, setNewGroupReservationData, summary, setIsSummaryModalOpen,
        setIsTicketPaymentGroupModalOpen, isCreateGroupModalOpen, setIsCreateGroupModalOpen,
        newGroupReservations, setIsConfirmBoucherModalOpen } = useAlly()

    const [tours, setTours] = useState<Tour[]>([])
    // Precio de cada tour en la ficha del aliado. undefined = todavía no se sabe (o no se pudo
    // cargar): en ese caso no se bloquea ningún tour y el paso 3 hace la validación de siempre.
    const [prices, setPrices] = useState<TourPrice[]>()
    const [tourId, setTourId] = useState<string>('')
    const [search, setSearch] = useState<string>('')
    const [date, setDate] = useState<Date | undefined>(
        newGroupReservationData?.reservation_date ? new Date(newGroupReservationData.reservation_date) : undefined
    )

    // Este modal sigue montado entre una reservación y la siguiente del mismo grupo, así que su
    // estado local conservaba el tour anterior. "Crear otra reservación" (ConfirmBoucher) limpia el
    // tour_id del contexto; acá se refleja en el selector para que el aliado elija de nuevo.
    useEffect(() => {
        if (!newGroupReservationData?.tour_id) setTourId('')
    }, [newGroupReservationData?.tour_id])

    useEffect(() => {
        async function getTours() {
            try {
                if (tours.length) return
                setIsLoading(true)
                const response = await ToursService.list()
                const options = response.data
                setTours(options)
            } catch (error) {
                setMessage({type: 'error', content: 'No se pudieron cargar los tours'})
            } finally {
                setIsLoading(false)
            }
        }
        getTours()
    }, [tours, setMessage])

    useEffect(() => {
        AllyService.me()
            .then(response => setPrices(response.data?.tour_prices || []))
            .catch(error => console.log(error))
    }, [])

    const priceOf = (id: string) => prices?.find(tp => tp.tour_id === id)?.price
    // Primero los tours que el aliado puede reservar; los que no tienen precio quedan al final.
    const sortedTours = [...tours].sort((a, b) => Number(!!priceOf(b.id)) - Number(!!priceOf(a.id)))
    const visibleTours = sortedTours.filter(tour => plain(tour.id).includes(plain(search.trim())))
    // Ya se envió al menos un tour en este flujo (se llegó con "Agregar otro tour" desde el voucher):
    // salir de acá no puede dejar al aliado sin su voucher.
    const lastReservation = newGroupReservations[newGroupReservations.length - 1]

    async function onSubmit(e: FormEvent<HTMLFormElement>) {
        e.preventDefault()
        if (!tourId || !date) {
            setTimeout(() => setMessage(undefined), 5000)
            setMessage({type: 'error', content: 'Elige un tour y una fecha para continuar'})
            return
        }
        setNewGroupReservationData(prevData => ({...prevData, tour_id: tourId, reservation_date: date}))
        setIsTicketPaymentGroupModalOpen(true)
    }

    function close() {
        setNewGroup([])
        setIsCreateGroupModalOpen(false)
    }

    // "Agregar otro tour" vació el tour del contexto; el voucher lo necesita para generarse.
    function backToVoucher() {
        setNewGroupReservationData(prevData => ({...prevData, tour_id: lastReservation.tour_id}))
        setIsConfirmBoucherModalOpen(true)
    }

    return (
        <WizardShell
            step={2}
            title="Elige un tour"
            flow={isCreateGroupModalOpen ? 'Agregar huéspedes' : 'Agregar tour'}
            onClose={lastReservation ? backToVoucher : close}
            closeIcon={lastReservation ? 'chevronLeft' : 'x'}
            closeLabel={lastReservation ? 'Volver al voucher' : 'Cerrar'}
            onSubmit={onSubmit}
            footer={
                <>
                    <Button type="submit" full size="lg" icon="arrowRight">Continuar</Button>
                    {lastReservation && <Button kind="ghost" full onClick={backToVoucher}>No agregar otro tour · volver al voucher</Button>}
                    {
                        summary && summary.length > 0 &&
                        <Button kind="quiet" full icon="list" onClick={() => setIsSummaryModalOpen(true)}>Ver resumen</Button>
                    }
                </>
            }
        >
            <Step>
                <div className="who">
                    <Icon name="users" size={16} />
                    <span>{newGroup.map(p => (p.name || '').split(' ')[0]).filter(Boolean).join(', ')}</span>
                </div>

                <Label className="section">Fecha</Label>
                <DayStrip day={date} onChange={setDate} allowPast={false} />

                <Label className="section">Tour</Label>
                <Field label="Buscar tour" hideLabel icon="search" type="search" placeholder="Buscar tour" autoComplete="off"
                    value={search} onChange={e => setSearch(e.target.value)} style={{ marginBottom: 11 }} />
                <div className="tours" role="radiogroup" aria-label="Tour">
                    {visibleTours.map(tour => {
                        const price = priceOf(tour.id)
                        const noPrice = prices !== undefined && !price
                        const selected = tour.id === tourId
                        return (
                            <button key={tour.id} type="button" role="radio" aria-checked={selected} disabled={noPrice}
                                className="tour" onClick={() => setTourId(tour.id)}>
                                <TourThumb tourId={tour.id} size={56} />
                                <span className="tour-info">
                                    <span className="tour-name">{tour.id}</span>
                                    {price ? <span className="chip">{clp(price)} / huésped</span> : null}
                                    {noPrice && <span className="chip off">Sin precio · contacta a la agencia</span>}
                                </span>
                                <span className="radio">{selected && <Icon name="check" size={14} stroke={3} />}</span>
                            </button>
                        )
                    })}
                </div>
                {tours.length > 0 && visibleTours.length === 0 && <p className="none">Ningún tour coincide con «{search.trim()}».</p>}
            </Step>
        </WizardShell>
    )
}

const Step = styled.div`
    .who {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 10px 13px;
        border-radius: ${m.radius.md};
        background: ${m.color.surface2};
        color: ${m.color.inkSoft};
        font-size: 13.5px;
        font-weight: 500;
        span {
            min-width: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
    }
    .section { margin: 18px 0 9px; }
    .none {
        margin: 14px 4px;
        font-size: 13.5px;
        color: ${m.color.inkFaint};
    }
    .tours {
        display: flex;
        flex-direction: column;
        gap: 11px;
    }
    .tour {
        display: flex;
        align-items: center;
        gap: 13px;
        padding: 11px;
        text-align: left;
        cursor: pointer;
        font-family: inherit;
        background: ${m.color.surface};
        border: 1.5px solid transparent;
        border-radius: ${m.radius.lg};
        box-shadow: ${m.shadow.card};
        &[aria-checked="true"] { border-color: ${m.color.primary}; }
        &:disabled {
            cursor: not-allowed;
            opacity: 0.6;
            box-shadow: none;
        }
    }
    .tour-info {
        flex: 1;
        min-width: 0;
    }
    .tour-name {
        display: block;
        font-family: ${m.font.display};
        font-weight: 600;
        font-size: 17px;
        color: ${m.color.ink};
    }
    .chip {
        display: inline-block;
        margin-top: 6px;
        padding: 2px 8px;
        border-radius: ${m.radius.pill};
        font-weight: 600;
        font-size: 12px;
        color: ${m.color.primaryDeep};
        background: ${m.color.primarySoft};
    }
    .chip.off {
        color: ${m.color.warn};
        background: ${m.color.warnSoft};
    }
    .radio {
        width: 24px;
        height: 24px;
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        border: 2px solid ${m.color.lineStrong};
        color: ${m.color.onPrimary};
    }
    .tour[aria-checked="true"] .radio {
        border-color: ${m.color.primary};
        background: ${m.color.primary};
    }
`
